/**
 * Goal/Boss autocomplete for Path hint rows.
 * Shows boss images and allows text search with tag matching.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { BOSS_GOALS, type GoalData } from './data/boss-goals-data';

interface GoalAutocompleteProps {
  value?: GoalData | null;
  onChange?: (goal: GoalData | null) => void;
  label?: string;
}

export function GoalAutocomplete({ value, onChange, label = 'Goal' }: GoalAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search) return BOSS_GOALS;
    const s = search.toLowerCase();
    return BOSS_GOALS.filter(
      (g) =>
        g.name.toLowerCase().includes(s) ||
        g.tags.some((t) => t.toLowerCase().includes(s))
    );
  }, [search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleSelect = (goal: GoalData) => {
    onChange?.(goal);
    setOpen(false);
    setSearch('');
  };

  // If a value is selected, show the boss image
  if (value) {
    return (
      <div
        className="flex items-center cursor-pointer hover:brightness-125 transition-all"
        onContextMenu={(e) => {
          e.preventDefault();
          onChange?.(null);
        }}
        onClick={() => onChange?.(null)}
        title={`${value.name} (click to clear)`}
      >
        <img src={value.img} alt={value.name} width={40} height={40} className="object-contain" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-[75px]">
      <div
        className="flex items-center gap-0.5 px-1 py-1 bg-gray-700/50 border border-gray-600/50 rounded cursor-pointer hover:border-gray-500 transition-colors"
        onClick={() => setOpen(!open)}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-xs text-white outline-none placeholder-gray-500 min-w-0 w-full"
            placeholder={label}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 text-xs text-gray-400 truncate">{label}</span>
        )}
        <ChevronDown size={12} className={`text-gray-500 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-[160px] max-h-[250px] overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl">
          {filtered.map((goal) => (
            <button
              key={goal.name}
              onClick={() => handleSelect(goal)}
              className="w-full text-left px-2 py-1 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <img src={goal.img} alt={goal.name} width={28} height={28} className="object-contain flex-shrink-0" />
              <span className="text-white truncate">{goal.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
